import { useEffect } from 'react';
import growthbook, {
  getGrowthbookDeviceId,
} from 'integrations/growthbook/growthbook';
import { isBrowser } from 'utils/BrowserUtils';

interface Props {
  ssrFeatures?: Record<string, any>;
}

// Module-level (not per-component) so the one-time sync init survives component
// remounts — e.g. the App Router remounts the [locale] layout on a locale
// switch. A per-component ref would reset and re-run the render-phase mutation
// below, which notifies the already-mounted GrowthBookProvider mid-render
// ("Cannot update a component while rendering a different component"). The
// GrowthBook instance is a client-side singleton, so a module flag matches its
// lifecycle. Guarded by isBrowser(), so it never runs during SSR.
let hasInitialised = false;

/**
 * Initialize GrowthBook with SSR features
 */
export function useGrowthBookInitialisation({ ssrFeatures }: Props = {}) {
  // Synchronous initialisation done on purpose to hook ssrFeatures into
  // growthbook instance immediately prior to first renders.
  if (isBrowser() && !hasInitialised) {
    if (ssrFeatures) {
      growthbook.setPayload({ features: ssrFeatures });
    }

    // Set deviceId immediately with an ephemeral GrowthBook Device Id
    // we generate locally on the device.
    //
    // Growthbook experiments can be configured to rely on a device id
    // to decide which variant to show. If the device id is not provided
    // fast enough, a default is first shown and once the device id is
    // available, the experiment variant is then applied / rendered,
    // causing flicker. By generating an ephemeral device id locally, we
    // prevent this flicker.
    const ephemeralId = getGrowthbookDeviceId();
    growthbook.setAttributes({ deviceId: ephemeralId });

    hasInitialised = true;
  }

  useEffect(() => {
    growthbook.init();
  }, []);
}
